// src/services/apiService.js
import axios from 'axios';
import { getCurrentToken } from '../utils/auth';

const isDev = process.env.NODE_ENV === 'development';
const API_URL = process.env.REACT_APP_API_URL || 'https://fcn-api-platform-hzcfayfsc0gebfhw.centralus-01.azurewebsites.net';
const PORTAL_URL = process.env.REACT_APP_PORTAL_URL || 'https://aplicacionesfcn.com';

const api = axios.create({
  baseURL: API_URL,
  headers: { 
    'Content-Type': 'application/json',
    'Accept': 'application/json'
  },
  withCredentials: false
});

// Interceptor REQUEST
api.interceptors.request.use(
  config => {
    const sessionId = getCurrentToken();
    if (sessionId) {
      config.headers.Authorization = `Bearer ${sessionId}`;
      config.headers['SessionId'] = sessionId;
    } else {
      console.warn('⚠️ No hay sessionId disponible');
      
      if (!isDev) {
        console.log('🔄 Sin sessionId en producción, redirigiendo al portal...');
        window.location.href = PORTAL_URL;
      }
    }
    return config;
  },
  error => Promise.reject(error)
);

// Interceptor RESPONSE
api.interceptors.response.use(
  response => {
    const data = response.data;

    if (typeof data === 'string') {
      if (data.includes('<!doctype html>') || data.includes('<html')) {
        const error = new Error('API returned HTML instead of JSON');
        error.response = response;
        throw error;
      }
    }

    if (data === null || data === undefined) return data;
    if (Array.isArray(data)) return data;
    if (data && typeof data === 'object' && 'data' in data) return data.data;
    return data;
  },
  error => {
    if (error.response?.status === 401 && !isDev) {
      localStorage.removeItem('sessionId');
      sessionStorage.clear();
      window.location.href = PORTAL_URL;
    }
    return Promise.reject(error);
  }
);

// Endpoints
export const authAPI = { 
  login: credentials => api.post('/auth/login', credentials) 
};

export const empleadoAPI = { 
  getAll: () => api.get('/empleado/api'), 
  getById: id => api.get(`/empleado/api/${id}`) 
};

export const departamentosAPI = { 
  getAll: () => api.get('/departamentos'), 
  getById: codigo => api.get(`/departamentos/${codigo}`) 
};

export const municipiosAPI = { 
  getAll: () => api.get('/municipios'), 
  getById: id => api.get(`/municipios/${id}`), 
  getByDepartamento: codigo => api.get(`/municipios/departamento/${codigo}`) 
};

export const maquinariaAPI = { 
  getAll: () => api.get('/maquina'), 
  getById: id => api.get(`/maquina/${id}`), 
  getTipos: () => api.get('/tipo') 
};

export const emergenciasAPI = { 
  getAll: () => api.get('/emergencias'), 
  getById: id => api.get(`/emergencias/${id}`), 
  create: data => api.post('/emergencias', data), 
  updateBatch: data => api.put('/emergencias', data) 
};

export const eventosEmergenciaAPI = { 
  getAll: () => api.get('/eventosEmergencia'), 
  getById: id => api.get(`/eventosEmergencia/${id}`), 
  create: data => api.post('/eventosEmergencia', data), 
  update: (id, data) => api.put(`/eventosEmergencia/${id}`, data) 
};

export const parametrosAPI = { 
  getTiposRed: () => api.get('/emergenciaParametros/tipos-red'), 
  getTiposEmergencia: () => api.get('/emergenciaParametros/tipos-emergencia') 
};

export const getAzureStorageContent = async folderPath => {
  let apiPath = folderPath.startsWith('/7-GESTION_DE_PROYECTOS') 
    ? folderPath 
    : `/7-GESTION_DE_PROYECTOS${folderPath}`;
  return await api.get(`/azureStorage${apiPath}`);
};

export const uploadFileToAzureStorage = async (folderPath, archivo) => {
  const formData = new FormData();
  formData.append('archivo', archivo);
  let apiPath = folderPath.startsWith('/7-GESTION_DE_PROYECTOS') 
    ? folderPath 
    : `/7-GESTION_DE_PROYECTOS${folderPath}`;
  return await api.post(`/azureStorage${apiPath}`, formData, { 
    headers: { 'Content-Type': 'multipart/form-data' } 
  });
};

export const getUsuarios = async () => api.get('/api');

export default api;
